import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom-v5-compat";
import { useModuleBEM } from "@b2bportal/core-themes";
import { useI18nContext, Trans } from "@hopper-b2b/i18n";
import { Page, PageLoadingScreen } from "@tenant/ui";

import { useSessionContext, INIT_SESSION_LINK } from "./SessionContext";
import styles from "./styles.module.scss";
import { ErrorPage } from "@/components";
import { ActionButton } from "@/components/ActionButton";

const PROD_URL = import.meta.env.VITE_PERKSPOT_URL_PRODUCTION;
const DEV_URL = import.meta.env.VITE_PERKSPOT_URL_DEVELOPMENT;
const LOCAL_URL = import.meta.env.VITE_PERKSPOT_URL_LOCAL;

export const StartSessionComponent = () => {
  const { t } = useI18nContext();
  const [searchParams] = useSearchParams();
  const { login, sessionState } = useSessionContext();

  useEffect(() => {
    const code = searchParams.get("code");
    const redirect_uri = searchParams.get("redirect_uri");
    const ps_sid = searchParams.get("ps_sid");

    if (ps_sid) {
      sessionStorage.setItem("ps_sid", ps_sid);
    }

    if (typeof code === "string" && sessionState === "idle") {
      const psParam = ps_sid ? `&ps_sid=${encodeURIComponent(ps_sid)}` : "";
      if (redirect_uri === "$prod" && PROD_URL !== "") {
        window.location.href =
          PROD_URL + "auth/startSession?code=" + code + psParam;
      } else if (redirect_uri === "$dev" && DEV_URL !== "") {
        window.location.href =
          DEV_URL + "auth/startSession?code=" + code + psParam;
      } else if (
        redirect_uri != null &&
        redirect_uri.startsWith("$local") &&
        LOCAL_URL !== ""
      ) {
        const originalRedirect = redirect_uri.replace("$local", "");
        if (
          originalRedirect !== "" &&
          originalRedirect !== "/" &&
          !originalRedirect.match(/^\/[a-zA-Z0-9-_]+/)
        ) {
          console.error("Invalid redirect_uri: " + redirect_uri);
          return;
        }
        window.location.href =
          LOCAL_URL +
          "auth/startSession?code=" +
          code +
          psParam +
          "&redirect_uri=" +
          encodeURIComponent(originalRedirect);
      } else {
        const successPath = redirect_uri ?? "/";
        const elevate = redirect_uri != null;
        login(code, successPath, { elevate });
      }
    }
  }, [login, sessionState, searchParams]);

  const [, cn] = useModuleBEM(styles, "StartSessionPage");
  return (
    <Page>
      <div className={cn("page-container")}>
        <div className={cn("page-content")}>
          <PageLoadingScreen
            isLoading
            className={cn("loading-screen")}
            text={t("auth.startSessionPage.loadingText")}
          />
        </div>
      </div>
    </Page>
  );
};

export const InvalidSessionPage = () => {
  const { t } = useI18nContext();
  const handleLoginClick = () => {
    window.location.href = INIT_SESSION_LINK;
  };

  const [, cn] = useModuleBEM(styles, "InvalidSessionPage");
  const { sessionError } = useSessionContext();

  let title = t("auth.invalidSessionError.title");
  let description = (
    <Trans
      i18nKey={"auth.invalidSessionError.description"}
      components={[<p />, <p />]}
    />
  );
  let cta = t("auth.loginCTA");

  if (sessionError === "start_session_failed") {
    title = t("auth.errorPage.title");
    description = (
      <Trans
        i18nKey={"auth.errorPage.description"}
        components={[<p />, <p />]}
      />
    );
    cta = t("auth.errorPage.retryCTA");
  }

  return (
    <ErrorPage showErrorIcon={false} title={title} className={cn("page")}>
      {description}
      <div>
        <ActionButton
          className={cn("signInButton")}
          onClick={handleLoginClick}
          message={cta}
        />
      </div>
    </ErrorPage>
  );
};

export const LogoutComponent = () => {
  const { t } = useI18nContext();
  const { logout } = useSessionContext();
  const navigate = useNavigate();

  useEffect(() => {
    logout();
    // navigate(PATH_BACK_TO_THE_HOPPER_LOUNGE);
  }, [logout, navigate]);

  return (
    <PageLoadingScreen className="loadingText" open text={t("loggingOut")} />
  );
};
